/**
 * CardEngine — aplica el efecto de una carta a un RunState.
 *
 * Lógica pura sin Phaser: nunca muta el `RunState` recibido, siempre
 * devuelve uno nuevo. Así, quien la use (ShopLogic, GameScene) puede
 * decidir libremente cuándo reemplazar su copia del estado.
 */

import type { CardDef, CardEffect, RunState, RunStats } from '../core/types';

/** Aplica un efecto de tipo `stat`: suma `add` al stat indicado (nunca resta ni sobrescribe). */
function applyStatEffect(
  effect: Extract<CardEffect, { kind: 'stat' }>,
  run: RunState
): RunState {
  const key = effect.stat;
  return {
    ...run,
    stats: { ...run.stats, [key]: run.stats[key] + effect.add } satisfies RunStats,
  };
}

/** Aplica un efecto de tipo `board`: agrega el modificador a la lista de mods activos del run. */
function applyBoardEffect(
  effect: Extract<CardEffect, { kind: 'board' }>,
  run: RunState
): RunState {
  return { ...run, boardMods: [...run.boardMods, effect.mod] };
}

/** Aplica un efecto de tipo `heal`: cura un % de la vida máxima, sin superar `maxHp`. */
function applyHealEffect(
  effect: Extract<CardEffect, { kind: 'heal' }>,
  run: RunState
): RunState {
  const healed = run.hp + run.maxHp * (effect.pct / 100);
  return { ...run, hp: Math.min(run.maxHp, Math.round(healed)) };
}

export const CardEngine = {
  /**
   * Aplica el efecto de `card` sobre `run` y devuelve el RunState resultante
   * (nuevo objeto; `run` no se modifica). Cubre los 3 `kind` de CardEffect.
   */
  apply(card: CardDef, run: RunState): RunState {
    const effect = card.effect;
    switch (effect.kind) {
      case 'stat':
        return applyStatEffect(effect, run);
      case 'board':
        return applyBoardEffect(effect, run);
      case 'heal':
        return applyHealEffect(effect, run);
      default: {
        // Exhaustividad: si se agrega un nuevo `kind` a CardEffect sin
        // actualizar este switch, esta línea deja de compilar.
        const exhaustive: never = effect;
        return exhaustive;
      }
    }
  },
};
